import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

// Advanced Coupon Management API hooks
export const useAllCoupons = (filters?: any) => {
  return useQuery({
    queryKey: ["/api/admin/coupons", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters) {
        Object.keys(filters).forEach(key => {
          if (filters[key] !== undefined) params.set(key, filters[key]);
        });
      }
      
      const response = await apiRequest(`/api/admin/coupons?${params}`, "GET");
      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreateAdvancedCoupon = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (couponData: AdvancedCouponData) =>
      apiRequest("/api/admin/coupons", "POST", couponData),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/coupons"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics/coupons"] });
      toast({
        title: "Coupon Created",
        description: `Successfully created coupon "${(data as any).code}".`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Creation Failed",
        description: error.message || "Failed to create coupon.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateCoupon = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: Partial<AdvancedCouponData> }) =>
      apiRequest(`/api/admin/coupons/${id}`, "PUT", updates),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/coupons"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics/coupons"] });
      toast({
        title: "Coupon Updated",
        description: `Successfully updated coupon "${(data as any).code}".`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update coupon.",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteCoupon = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: number) =>
      apiRequest(`/api/admin/coupons/${id}`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/coupons"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics/coupons"] });
      toast({
        title: "Coupon Deleted",
        description: "Successfully deleted coupon.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Deletion Failed",
        description: error.message || "Failed to delete coupon.",
        variant: "destructive",
      });
    },
  });
};

export const useCouponUsageStats = (couponId: number) => {
  return useQuery({
    queryKey: ["/api/admin/coupons", couponId, "stats"],
    queryFn: async () => {
      const response = await apiRequest(`/api/admin/coupons/${couponId}/stats`, "GET");
      return response.json();
    },
    enabled: !!couponId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useValidateCoupon = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ code }: { code: string }) =>
      apiRequest("/api/coupons/validate", "POST", { code }),
    onError: (error: any) => {
      toast({
        title: "Validation Failed",
        description: error.message || "Failed to validate coupon.",
        variant: "destructive",
      });
    },
  });
};

export const useApplyCoupon = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ couponId, orderData }: { couponId: number; orderData: any }) =>
      apiRequest("/api/coupons/apply", "POST", { couponId, orderData }),
    onSuccess: () => {
      toast({
        title: "Coupon Applied",
        description: "Coupon has been successfully applied to the order.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Application Failed",
        description: error.message || "Failed to apply coupon.",
        variant: "destructive",
      });
    },
  });
};

// Advanced Coupon types
export interface AdvancedCouponData {
  code: string;
  name: string;
  description?: string;
  discountType: "percentage" | "fixed" | "credits" | "plan_upgrade";
  discountValue: number;
  validFrom: string;
  validUntil: string;
  usageLimit?: number;
  perUserLimit?: number;
  minimumPurchase?: number;
  planRestrictions?: string[];
  geographicRestrictions?: string[];
  isStackable?: boolean;
  autoApply?: boolean;
  campaignId?: string;
}

export interface CouponUsageStats {
  code: string;
  usageLimit?: number;
  totalUsage: number;
  uniqueUsers: number;
  totalDiscountGiven: number;
  totalOrderValue: number;
  averageDiscountPerUse: number;
}

export interface CouponValidationResult {
  valid: boolean;
  coupon?: any;
  message: string;
}